import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import ChartComponent from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import Img from '../../../../Img';
import { InfoTrigger, InfoPopup } from '../ScatterSection';
import LandscapeHistogramOverlay from '../LandscapeHistogramOverlay';
import LockedChartOverlay from '../LockedChartOverlay';
import useIsMobile from '../../../../hooks/useIsMobile';
import { pluralize } from '../../../../../utils/formatting';
import {
    buildHistogramData,
    buildHistogramOptions,
    buildHistogramAnchorPlugin,
    formatRangeLabel,
    histogramLocked,
} from '../performanceStatsUtils';
import { ROUNDED_GRADIENT_BAR_TYPE, registerRoundedGradientBarType } from '../roundedGradientBar';
import styles from '../performanceStats.scss';

registerRoundedGradientBarType();

function HistogramSection({ singles, currentUtr, isLocked, onLockClick, 'data-testid': dataTestId }) {
    const [showDistributionInfo, setShowDistributionInfo] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const { isMobileWidth } = useIsMobile();

    const histogram = singles?.histogram;
    const showTransitionalState = histogramLocked(histogram);
    const showRotateTrigger = isMobileWidth && !isLocked && !showTransitionalState;

    useEffect(() => {
        if (isExpanded && !showRotateTrigger) {
            setIsExpanded(false);
        }
    }, [isExpanded, showRotateTrigger]);
    const histogramData = useMemo(() => buildHistogramData(histogram), [histogram]);
    const histogramOptions = useMemo(
        () => buildHistogramOptions(histogram, isMobileWidth),
        [histogram, isMobileWidth],
    );
    const chartPlugins = useMemo(
        () => [ChartDataLabels, buildHistogramAnchorPlugin(histogram, currentUtr)],
        [histogram, currentUtr],
    );
    const rangeLabel = formatRangeLabel(singles);
    const distributionInfoTestId = dataTestId && `modal.${dataTestId}.distribution`;

    const chartContent = showTransitionalState ? (
        <div className={styles.transitionalState} data-testid={dataTestId && `${dataTestId}.transitional`}>
            {`Play ${histogram?.matchesRequired} ${pluralize('match', histogram?.matchesRequired)} in the last 12 months to unlock your Performance Distribution chart.`}
        </div>
    ) : (
        <div className={styles.chartCard}>
            {rangeLabel && (
                <div className={styles.chartHeaderRow} data-testid={dataTestId && `${dataTestId}.range`}>
                    <span className={styles.chartHeaderLabel} data-testid={dataTestId && `${dataTestId}.range.label`}>Performance Range:</span>
                    <span className={styles.chartHeaderValue} data-testid={dataTestId && `${dataTestId}.range.value`}>{rangeLabel}</span>
                </div>
            )}
            <div className={styles.chartCanvas}>
                <ChartComponent
                    type={ROUNDED_GRADIENT_BAR_TYPE}
                    data={histogramData}
                    options={histogramOptions}
                    plugins={chartPlugins}
                    height={360}
                />
            </div>
            {showRotateTrigger && (
                <div className={`${styles.legendTriggerRow} ${styles.footerRowEnd}`}>
                    <button
                        type="button"
                        className={styles.expandTriggerBtn}
                        onClick={() => setIsExpanded(true)}
                        data-testid={dataTestId && `${dataTestId}.expand-btn`}
                    >
                        <Img src="/img/icons/rotate-to-landscape-icon.svg" alt="Expand chart" className={styles.expandTriggerIcon} data-testid={dataTestId && `${dataTestId}.expand-btn.icon`} />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <section className={styles.chartBlock} data-testid={dataTestId}>
            <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle} data-testid={dataTestId && `${dataTestId}.title`}>Performance Distribution</h4>
                <InfoTrigger onOpen={() => setShowDistributionInfo(true)} data-testid={dataTestId && `${dataTestId}.info-btn`} />
            </div>
            <p className={styles.sectionSubtitle} data-testid={dataTestId && `${dataTestId}.subtitle`}>
                How often you play above, at, or below your level over your last 40 matches.
            </p>
            {isLocked ? (
                <div className={styles.lockedSection}>
                    <div className={styles.lockedBlur}>
                        {chartContent}
                    </div>
                    <LockedChartOverlay onClick={onLockClick} data-testid={dataTestId && `${dataTestId}.lock-btn`} />
                </div>
            ) : chartContent}
            {isExpanded && showRotateTrigger && (
                <LandscapeHistogramOverlay
                    singles={singles}
                    currentUtr={currentUtr}
                    rangeLabel={rangeLabel}
                    onClose={() => setIsExpanded(false)}
                    data-testid={dataTestId && `modal.${dataTestId}.landscape`}
                />
            )}
            <InfoPopup
                isOpen={showDistributionInfo}
                onClose={() => setShowDistributionInfo(false)}
                title="About Performance Distribution"
                data-testid={distributionInfoTestId}
            >
                <p data-testid={distributionInfoTestId && `${distributionInfoTestId}.intro`}>The chart below displays 5 bins that span your range of match performances.</p>
                <ul data-testid={distributionInfoTestId && `${distributionInfoTestId}.points`}>
                    <li data-testid={distributionInfoTestId && `${distributionInfoTestId}.points.bar-height`}>The height of each bar shows the number of matches that you have played, for which you performed at a rating within that bin.</li>
                    <li data-testid={distributionInfoTestId && `${distributionInfoTestId}.points.compactness`}>A more compact distribution indicates higher consistency amongst match performances, whereas a more spread out distribution indicates lower consistency amongst match performances.</li>
                    <li data-testid={distributionInfoTestId && `${distributionInfoTestId}.points.range`}>A more narrow range of performances indicates higher consistency amongst match performances, whereas a broader range indicates lower consistency amongst match performance.</li>
                    <li data-testid={distributionInfoTestId && `${distributionInfoTestId}.points.right-concentration`}>Having a higher concentration of bars to the right of your rating indicates a player that is over performing their rating often, and can expect their rating to increase.</li>
                    <li data-testid={distributionInfoTestId && `${distributionInfoTestId}.points.left-concentration`}>Having a higher concentration of bars to the left of your rating indicates a player that is more often underperforming their rating, and can expect their rating to decrease.</li>
                </ul>
            </InfoPopup>
        </section>
    );
}

HistogramSection.propTypes = {
    singles: PropTypes.shape({
        histogram: PropTypes.shape({
            data: PropTypes.arrayOf(PropTypes.shape({})),
            matchesRequired: PropTypes.number,
            resultCount: PropTypes.number,
        }),
    }),
    currentUtr: PropTypes.number,
    isLocked: PropTypes.bool,
    onLockClick: PropTypes.func,
    'data-testid': PropTypes.string,
};

HistogramSection.defaultProps = {
    singles: null,
    currentUtr: null,
    isLocked: false,
    onLockClick: null,
    'data-testid': null,
};

export default HistogramSection;
